import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { BuyCreditsDialog } from '@/components/payments/BuyCreditsDialog';
import type {
  RedeemCouponResponse,
  ResourcePlan,
} from '@claude-code-api/shared';

interface SubscriptionData {
  subscription: {
    id: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    creditsCharged: number;
    autoRenew: boolean;
    stripeAutoChargeEnabled?: boolean;
    subscriptionCreditBalance?: number;
    purchasedCreditBalance?: number;
    totalCreditBalance?: number;
    pendingPlan: {
      id: string;
      name: string;
      cpuCores: number;
      memoryGb: number;
      storageGb: number;
      monthlyCredits: number;
      monthlyPriceUsd: number;
    } | null;
    pendingPlanEffectiveAt: string | null;
    plan: {
      id: string;
      name: string;
      cpuCores: number;
      memoryGb: number;
      storageGb: number;
      monthlyCredits: number;
      monthlyPriceUsd: number;
    } | null;
  } | null;
  creditBalances?: {
    subscriptionCreditBalance: number;
    purchasedCreditBalance: number;
    totalCreditBalance: number;
  };
  paymentStatus?: {
    hasIssue: boolean;
    code: string;
    message: string;
    retryEnabled?: boolean;
    latestBillingStatus: string | null;
    latestBillingAt: string | null;
    latestFailedAt: string | null;
    latestFailedPlanName: string | null;
    latestFailedInvoiceUrl?: string | null;
    canRecoverInPortal?: boolean;
  };
  stripeDebug?: {
    enabled: boolean;
    subscriptionId: string | null;
    subscriptionStatus: string | null;
    stripeAutoChargeEnabled: boolean;
    stripeCustomerIdMasked: string | null;
    stripePaymentMethodIdMasked: string | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    pendingPlanId: string | null;
    pendingPlanEffectiveAt: string | null;
    latestBillingExternalRef: string | null;
    latestBillingStatus: string | null;
    latestBillingCreatedAt: string | null;
    latestFailedBillingExternalRef: string | null;
    latestFailedBillingCreatedAt: string | null;
  };
}

interface SubscriptionPageProps {
  embedded?: boolean;
}

export default function SubscriptionPage({
  embedded = false,
}: SubscriptionPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['subscription', 'usage']);
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<ResourcePlan[]>([]);
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [redirectingToCheckout, setRedirectingToCheckout] = useState<
    string | null
  >(null);
  const [confirmingCheckout, setConfirmingCheckout] = useState(false);
  const [updatingAutoRenew, setUpdatingAutoRenew] = useState(false);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const [openingRecoveryPortal, setOpeningRecoveryPortal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);
  const handledStripeStateRef = useRef<string | null>(null);

  const fetchData = async () => {
    try {
      const [plansRes, meRes] = await Promise.all([
        fetch('/api/subscriptions/plans', { credentials: 'include' }),
        fetch('/api/subscriptions/me', { credentials: 'include' }),
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData.data || []);
      }

      if (meRes.ok) {
        const meData = await meRes.json();
        setData(meData.data || null);
      }
    } catch {
      toast.error(t('subscription:errors.loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const hadSubscriptionBeforeCheckout = Boolean(data?.subscription);
    const params = new URLSearchParams(window.location.search);
    const stripeState = params.get('stripe');
    const sessionId = params.get('session_id');
    const marker = `${stripeState || ''}:${sessionId || ''}`;

    if (handledStripeStateRef.current === marker) return;

    if (
      stripeState === 'cancel' ||
      stripeState === 'autorenew_cancel' ||
      stripeState === 'credit_cancel'
    ) {
      handledStripeStateRef.current = marker;
      toast.message(
        stripeState === 'autorenew_cancel'
          ? t('subscription:messages.autoRenewSetupCancelled')
          : stripeState === 'credit_cancel'
            ? t('usage:messages.checkoutCancelled')
            : t('subscription:messages.checkoutCancelled'),
      );
      params.delete('stripe');
      params.delete('session_id');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', next);
      return;
    }

    if (
      (stripeState !== 'success' &&
        stripeState !== 'autorenew_success' &&
        stripeState !== 'credit_success') ||
      !sessionId
    )
      return;
    handledStripeStateRef.current = marker;

    const confirm = async () => {
      setConfirmingCheckout(true);
      try {
        const confirmPath =
          stripeState === 'autorenew_success'
            ? '/api/subscriptions/auto-renew/confirm'
            : stripeState === 'credit_success'
              ? '/api/credits/packs/checkout/confirm'
              : '/api/subscriptions/checkout/confirm';

        const res = await fetch(confirmPath, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ sessionId }),
        });
        const result = await res.json();
        if (!res.ok) {
          toast.error(
            result.error || t('subscription:errors.confirmPaymentFailed'),
          );
          return;
        }
        toast.success(
          stripeState === 'autorenew_success'
            ? t('subscription:messages.autoRenewEnabledSavedCard')
            : stripeState === 'credit_success'
              ? t('usage:messages.confirmSuccess')
              : t('subscription:messages.paymentConfirmedUpdated'),
        );
        await refreshUser();
        await fetchData();
        if (stripeState === 'success' && !hadSubscriptionBeforeCheckout) {
          navigate('/', { replace: true });
        }
      } catch {
        toast.error(
          stripeState === 'autorenew_success'
            ? t('subscription:errors.confirmAutoRenewSetupFailed')
            : stripeState === 'credit_success'
              ? t('subscription:errors.confirmCreditPurchaseFailed')
              : t('subscription:errors.confirmPaymentFailed'),
        );
      } finally {
        setConfirmingCheckout(false);
        params.delete('stripe');
        params.delete('session_id');
        const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, '', next);
      }
    };

    void confirm();
  }, [data?.subscription, navigate, refreshUser, t]);

  const startStripeCheckout = async (
    planId: string,
    action: 'subscribe' | 'change-plan',
  ) => {
    setRedirectingToCheckout(planId);
    try {
      const res = await fetch('/api/subscriptions/checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ planId, action }),
      });
      const result = await res.json();

      if (!res.ok) {
        if (res.status === 503) {
          toast.error(t('subscription:errors.stripeNotConfigured'));
        } else {
          toast.error(
            result.error || t('subscription:errors.createCheckoutFailed'),
          );
        }
        return;
      }

      const payload = result?.data || {};
      if (!payload.requiresPayment) {
        if (payload.noChange) {
          toast.success(t('subscription:messages.noPlanChangeNeeded'));
        } else if (payload.scheduledDowngrade) {
          toast.success(
            payload.pendingPlanName
              ? t('subscription:messages.downgradeScheduledNamed', {
                  planName: payload.pendingPlanName,
                })
              : t('subscription:messages.downgradeScheduled'),
          );
        } else if (payload.noChargeUpgrade || payload.applied) {
          toast.success(
            payload.planName
              ? t('subscription:messages.switchedToPlan', {
                  planName: payload.planName,
                })
              : t('subscription:messages.switchedToNewPlan'),
          );
        } else {
          toast.message(t('subscription:messages.noPaymentRequired'));
        }
        await refreshUser();
        await fetchData();
        return;
      }

      if (!payload.checkoutUrl) {
        toast.error(t('subscription:errors.checkoutUrlMissing'));
        return;
      }

      window.location.href = String(payload.checkoutUrl);
    } catch {
      toast.error(t('subscription:errors.createCheckoutFailed'));
    } finally {
      setRedirectingToCheckout(null);
    }
  };

  const handleAutoRenewToggle = async (nextValue: boolean) => {
    setUpdatingAutoRenew(true);
    try {
      const res = await fetch('/api/subscriptions/auto-renew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ autoRenew: nextValue }),
      });
      const result = await res.json();
      if (res.ok) {
        if (result?.data?.requiresSetup && result?.data?.checkoutUrl) {
          toast.message(t('subscription:messages.completeCardSetup'));
          window.location.href = String(result.data.checkoutUrl);
          return;
        }
        toast.success(
          nextValue
            ? t('subscription:messages.autoRenewEnabled')
            : t('subscription:messages.autoRenewDisabled'),
        );
        await fetchData();
      } else {
        toast.error(
          result.error || t('subscription:errors.updateAutoRenewFailed'),
        );
      }
    } catch {
      toast.error(t('subscription:errors.updateAutoRenewFailed'));
    } finally {
      setUpdatingAutoRenew(false);
    }
  };

  const cancelPendingDowngrade = async () => {
    setCancellingDowngrade(true);
    try {
      const res = await fetch('/api/subscriptions/cancel-pending-downgrade', {
        method: 'POST',
        credentials: 'include',
      });
      const result = await res.json();
      if (!res.ok) {
        toast.error(
          result.error ||
            t('subscription:errors.cancelScheduledDowngradeFailed'),
        );
        return;
      }
      toast.success(t('subscription:messages.scheduledDowngradeCancelled'));
      await fetchData();
    } catch {
      toast.error(t('subscription:errors.cancelScheduledDowngradeFailed'));
    } finally {
      setCancellingDowngrade(false);
    }
  };

  const openRecoveryPortal = async () => {
    setOpeningRecoveryPortal(true);
    try {
      const res = await fetch('/api/subscriptions/recovery-portal', {
        method: 'POST',
        credentials: 'include',
      });
      const result = await res.json();
      if (!res.ok || !result?.data?.url) {
        toast.error(
          result?.error || t('subscription:errors.openBillingPortalFailed'),
        );
        return;
      }
      window.location.href = String(result.data.url);
    } catch {
      toast.error(t('subscription:errors.openBillingPortalFailed'));
    } finally {
      setOpeningRecoveryPortal(false);
    }
  };

  const getCouponErrorMessage = (code?: string, fallback?: string) => {
    switch (code) {
      case 'coupon_required':
        return t('subscription:coupon.errors.required');
      case 'coupon_invalid':
        return t('subscription:coupon.errors.invalid');
      case 'coupon_inactive':
        return t('subscription:coupon.errors.inactive');
      case 'coupon_expired':
        return t('subscription:coupon.errors.expired');
      case 'coupon_exhausted':
        return t('subscription:coupon.errors.exhausted');
      case 'coupon_already_redeemed':
        return t('subscription:coupon.errors.alreadyRedeemed');
      case 'coupon_plan_unavailable':
      case 'plan_not_found':
        return t('subscription:coupon.errors.planUnavailable');
      case 'coupon_plan_mismatch':
        return t('subscription:coupon.errors.planMismatch');
      case 'approval_required':
        return t('subscription:coupon.errors.approvalRequired');
      default:
        return fallback || t('subscription:coupon.errors.redeem');
    }
  };

  const redeemCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      toast.error(t('subscription:coupon.errors.required'));
      return;
    }

    setRedeemingCoupon(true);
    try {
      const res = await fetch('/api/subscriptions/redeem-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      const result = await res.json();
      if (!res.ok) {
        toast.error(getCouponErrorMessage(result.code, result.error));
        return;
      }

      const data = result.data as RedeemCouponResponse;
      toast.success(
        t('subscription:coupon.messages.redeemed', {
          planName: data.planName,
          date: new Date(data.periodEnd).toLocaleDateString(),
        }),
      );
      setCouponCode('');
      await refreshUser();
      await fetchData();
    } catch {
      toast.error(t('subscription:coupon.errors.redeem'));
    } finally {
      setRedeemingCoupon(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'space-y-4' : 'p-6 space-y-4'}>
        {!embedded ? <Skeleton className="h-8 w-48" /> : null}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const sub = data?.subscription;
  const isActive = sub?.status === 'active';
  const canSubscribe = !sub || !isActive;
  const paymentStatus = data?.paymentStatus;
  const stripeDebug = data?.stripeDebug;

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {!embedded ? (
        <PageHeader
          title={t('subscription:title')}
          description={t('subscription:description')}
        />
      ) : null}
      {confirmingCheckout && (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
          {t('subscription:confirmingPayment')}
        </div>
      )}
      {paymentStatus?.hasIssue && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">
            {t('subscription:statusCard.paymentIssueDetected')}
          </p>
          <p>{paymentStatus.message}</p>
          {paymentStatus.retryEnabled && (
            <p className="mt-1 text-xs">
              {t('subscription:statusCard.retryAfterFix')}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {paymentStatus.latestFailedInvoiceUrl && (
              <Button size="sm" variant="outline" asChild>
                <a
                  href={paymentStatus.latestFailedInvoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('subscription:statusCard.openFailedInvoice')}
                </a>
              </Button>
            )}
            {paymentStatus.canRecoverInPortal && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void openRecoveryPortal();
                }}
                disabled={openingRecoveryPortal}
              >
                {openingRecoveryPortal
                  ? t('subscription:statusCard.openingBillingPortal')
                  : t('subscription:statusCard.fixPaymentMethod')}
              </Button>
            )}
            {!paymentStatus.canRecoverInPortal && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void handleAutoRenewToggle(true);
                }}
                disabled={updatingAutoRenew}
              >
                {updatingAutoRenew
                  ? t('subscription:statusCard.preparingSetup')
                  : t('subscription:statusCard.setupCardForAutoRenew')}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="coupon-code">
          {t('subscription:coupon.label')}
        </Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="flex-1">
            <Input
              id="coupon-code"
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value)}
              placeholder={t('subscription:coupon.placeholder')}
              autoComplete="off"
            />
          </div>
          <Button
            className="sm:self-end"
            onClick={() => {
              void redeemCoupon();
            }}
            disabled={redeemingCoupon || !couponCode.trim()}
          >
            {redeemingCoupon
              ? t('subscription:coupon.redeeming')
              : t('subscription:coupon.redeem')}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {t('subscription:coupon.description')}
        </p>
      </div>

      {/* Plan cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          {isActive
            ? t('subscription:plans.changePlan')
            : t('subscription:plans.choosePlan')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const isCurrent = sub?.plan?.id === plan.id;
            const isCurrentActive = isCurrent && isActive;
            const isCurrentInactive = isCurrent && !isActive;
            const isScheduled = sub?.pendingPlan?.id === plan.id;
            return (
              <Card key={plan.id} className={isCurrent ? 'border-primary' : ''}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{plan.name}</CardTitle>
                    {isCurrentActive && (
                      <Badge variant="success">
                        {t('subscription:plans.current')}
                      </Badge>
                    )}
                    {isCurrentInactive && (
                      <Badge variant="warning">
                        {sub?.status === 'suspended'
                          ? t('subscription:plans.suspended')
                          : t('subscription:plans.inactive')}
                      </Badge>
                    )}
                    {!isCurrent && isScheduled && (
                      <Badge variant="secondary">
                        {t('subscription:plans.scheduled')}
                      </Badge>
                    )}
                    {plan.isDefault && !isCurrent && (
                      <Badge variant="outline">
                        {t('subscription:plans.popular')}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-3xl font-bold">
                    ${Number(plan.monthlyPriceUsd).toFixed(0)}
                    <span className="text-sm font-normal text-muted-foreground">
                      {t('subscription:plans.perMonthPrice')}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t('subscription:plans.creditsPerMonth', {
                      count: Number(plan.monthlyCredits),
                    })}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{t('subscription:plans.cpuCores')}</span>
                      <span className="font-medium">
                        {Number(plan.cpuCores)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('subscription:plans.memory')}</span>
                      <span className="font-medium">
                        {Number(plan.memoryGb)} {t('subscription:resources.gb')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('subscription:plans.storage')}</span>
                      <span className="font-medium">
                        {Number(plan.storageGb)}{' '}
                        {t('subscription:resources.gb')}
                      </span>
                    </div>
                  </div>
                  {canSubscribe ? (
                    <Button
                      className="w-full"
                      onClick={() => {
                        void startStripeCheckout(plan.id, 'subscribe');
                      }}
                      disabled={redirectingToCheckout === plan.id}
                    >
                      {redirectingToCheckout === plan.id
                        ? t('subscription:plans.redirectingToCheckout')
                        : isCurrentInactive
                          ? t('subscription:plans.renewCurrentPlan')
                          : t('subscription:plans.subscribe')}
                    </Button>
                  ) : isScheduled ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => {
                        void cancelPendingDowngrade();
                      }}
                      disabled={cancellingDowngrade}
                    >
                      {cancellingDowngrade
                        ? t('subscription:plans.cancelling')
                        : t('subscription:plans.cancelDowngrade')}
                    </Button>
                  ) : !isCurrent ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => {
                        void startStripeCheckout(plan.id, 'change-plan');
                      }}
                      disabled={redirectingToCheckout === plan.id}
                    >
                      {redirectingToCheckout === plan.id
                        ? t('subscription:plans.redirectingToCheckout')
                        : t('subscription:plans.switchToPlan', {
                            planName: plan.name,
                          })}
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
          {/* Enterprise — contact sales */}
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle>{t('subscription:enterprise.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-3xl font-bold">
                {t('subscription:enterprise.custom')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t('subscription:enterprise.tailored')}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>{t('subscription:plans.cpuCores')}</span>
                  <span className="font-medium">
                    {t('subscription:enterprise.unlimited')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('subscription:plans.memory')}</span>
                  <span className="font-medium">
                    {t('subscription:enterprise.unlimited')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('subscription:plans.storage')}</span>
                  <span className="font-medium">
                    {t('subscription:enterprise.unlimited')}
                  </span>
                </div>
              </div>
              <Button className="w-full" variant="outline" asChild>
                <a href="mailto:sales@gift.dev">
                  {t('subscription:enterprise.contactSales')}
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <BuyCreditsDialog
        open={buyCreditsOpen}
        onOpenChange={setBuyCreditsOpen}
        canPurchase={Boolean(isActive)}
        reason={isActive ? null : t('usage:messages.inactiveReason')}
        returnPath={window.location.pathname}
      />
      {stripeDebug && (
        <details className="rounded-md border bg-surface-0 p-3 text-xs">
          <summary className="cursor-pointer font-medium text-muted-foreground">
            {t('subscription:debug.title')}
          </summary>
          <pre className="mt-2 whitespace-pre-wrap break-words text-[11px] leading-5">
            {JSON.stringify(stripeDebug, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
